import { useCallback, useEffect, useState } from 'react';

// Read the Rails CSRF token from the meta tag the layout renders.
function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.content || '';
}

// useQboAccounts — fetches the QBO chart-of-accounts list and the user's
// saved expense → category mappings, in one parallel request, whenever the
// connection becomes active. Extracted from SucceedoIntegrations so the
// fetch + state can be exercised in isolation (mock fetch in jest-dom).
//
// The backend caches `expense_accounts` per-connection on the server side
// (see Quickbooks::AccountsService — todo 132 BE). Apollo's HTTP cache
// covers the rest, so this hook stays a plain `fetch` — no extra layer here.
//
// Also exposes a `saveMappings` action that POSTs to `expenseMappingsUrl`
// using the accounts/mappings the hook already holds.
export default function useQboAccounts({
  connected,
  expenseAccountsUrl,
  expenseMappingsUrl,
}) {
  const [accounts, setAccounts] = useState([]);
  const [mappings, setMappings] = useState({});
  const [loading, setLoading] = useState(false);

  const refetch = useCallback(async () => {
    if (!connected || !expenseAccountsUrl || !expenseMappingsUrl) return;
    setLoading(true);
    try {
      const [accountsRes, mappingsRes] = await Promise.all([
        fetch(expenseAccountsUrl, { headers: { Accept: 'application/json' } }),
        fetch(expenseMappingsUrl, { headers: { Accept: 'application/json' } }),
      ]);

      if (accountsRes.ok) {
        const data = await accountsRes.json();
        setAccounts(data.accounts || []);
      }

      if (mappingsRes.ok) {
        const data = await mappingsRes.json();
        const map = {};
        (data.mappings || []).forEach((m) => {
          map[m.qbo_account_id] = m.expense_category_key;
        });
        setMappings(map);
      }
    } catch (e) {
      // Swallow — UI stays in "0 accounts" state, the connection card still
      // renders. Logged for observability.
      // eslint-disable-next-line no-console
      console.error('Failed to fetch QBO accounts/mappings', e);
    } finally {
      setLoading(false);
    }
  }, [connected, expenseAccountsUrl, expenseMappingsUrl]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const updateMapping = useCallback((accountId, categoryKey) => {
    setMappings((prev) => {
      const next = { ...prev };
      if (categoryKey) {
        next[accountId] = categoryKey;
      } else {
        delete next[accountId];
      }
      return next;
    });
  }, []);

  // Returns a plain object so the caller can react to outcomes:
  // { ok: true, mappedCount } | { ok: false, error }
  const saveMappings = useCallback(async () => {
    const mappingsArray = accounts
      .filter((a) => mappings[a.id])
      .map((a) => ({ qbo_account_id: a.id, qbo_account_name: a.name, expense_category_key: mappings[a.id] }));
    try {
      const res = await fetch(expenseMappingsUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': csrfToken() },
        body: JSON.stringify({ mappings: mappingsArray }),
      });
      if (res.ok) return { ok: true, mappedCount: mappingsArray.length };
      const data = await res.json();
      return { ok: false, error: data.error || 'Failed to save mappings.' };
    } catch (e) {
      return { ok: false, error: 'Failed to save mappings. Please try again.' };
    }
  }, [accounts, mappings, expenseMappingsUrl]);

  return {
    accounts,
    mappings,
    loading,
    refetch,
    updateMapping,
    setMappings,
    saveMappings,
  };
}
